import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { formatDate } from '../../../utils/Utils';
import Tooltip from '../../../components/ui/Tooltip';
import { formatMoney, formatRate, EMPTY } from '../../ats-requisitions/recruitingCost';

// SPEC F3 — satırda kaç maliyet kaydı GEÇERLİ para biriminde değil (ayar
// değiştikten önce girilenler). Toplama GİRMEZLER; kullanıcı bunu görmeli.
export const otherCurrencyCountOf = (row) => Number(row?.otherCurrencyCount || 0);

// AtsRequisition.Status enum değerleri — bkz. backend/Models/AtsRequisition.cs.
// Etiketler mevcut ats.requisition.status.* anahtarlarını KULLANIR (yeni anahtar
// eklenmedi — ats-requisitions ekranıyla aynı sözlük, çeviri çakışması riski yok).
export const REQUISITION_STATUSES = [
  'draft',
  'pending_approval',
  'approved',
  'posting_opened',
  'rejected',
  'on_hold',
  'filled',
  'cancelled',
];

// Kadro bazlı işe alım raporu kolonları. Backend AtsRecruitingReportDataAccess
// ApplySort switch'i yalnız "status" | "approvedat" sıralamasını tanır (varsayılan
// dal createDate) — `sortKey` bunlarla eşlenir, diğer kolonlarda sort kapalıdır.
export function useAtsRecruitingReportColumns(data) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  return useMemo(() => {
    const statusLabel = (s) => t(`ats.requisition.status.${s}`, { defaultValue: s });
    const numCell = (v) => Number(v || 0);

    return [
      {
        name: 'departmentName',
        label: t('atsReport.department'),
        options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
      },
      {
        name: 'positionTitleName',
        label: t('atsReport.position'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return '-';
            const code = row.positionCode ? `[${row.positionCode}] ` : '';
            return (code + (row.positionTitleName || '')).trim() || '-';
          },
        },
      },
      {
        name: 'hiringManagerName',
        label: t('atsReport.hiringManager'),
        options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
      },
      {
        name: 'openedAt',
        label: t('atsReport.openedAt'),
        options: {
          filter: false, sort: true, sortKey: 'createdate',
          customBodyRender: (v) => (v ? formatDate(v, lang) : '-'),
        },
      },
      {
        name: 'status',
        label: t('atsReport.statusLabel'),
        options: {
          filter: false, sort: true, sortKey: 'status',
          customBodyRender: statusLabel,
        },
      },
      {
        name: 'applicationCount',
        label: t('atsReport.applicationCount'),
        options: { filter: false, sort: false, customBodyRender: numCell },
      },
      {
        name: 'interviewCount',
        label: t('atsReport.interviewCount'),
        options: { filter: false, sort: false, customBodyRender: numCell },
      },
      {
        name: 'offerCount',
        label: t('atsReport.offerCount'),
        options: { filter: false, sort: false, customBodyRender: numCell },
      },
      {
        name: 'hiredCount',
        label: t('atsReport.hiredCount'),
        options: { filter: false, sort: false, customBodyRender: numCell },
      },
      {
        // 0 "aynı gün doldu" demek, NULL "henüz doldurulmadı/onaylanmadı" demek —
        // backend NULL için 0 ASLA DÖNMEZ (bkz. AtsRecruitingReportDataAccess).
        name: 'fillDurationDays',
        label: t('atsReport.fillDurationDays'),
        options: {
          filter: false, sort: false,
          customBodyRender: (v) => (v === null || v === undefined ? '-' : Number(v)),
        },
      },
      {
        // SPEC F3 — yalnız GEÇERLİ para birimindeki kayıtların toplamı.
        name: 'totalCost',
        label: t('atsReport.totalCost'),
        options: {
          filter: false, sort: false,
          customBodyRender: (v, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const text = formatMoney(v, row?.costCurrency, lang);
            const other = otherCurrencyCountOf(row);
            if (!other) return text;
            const tip = t('atsReport.otherCurrencyTooltip', { count: other, currency: row?.costCurrency || '' });
            return (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} data-role="ats-report-other-currency">
                {text}
                <Tooltip text={tip}>
                  <AlertTriangle size={13} color="#B26A00" aria-label={tip} />
                </Tooltip>
              </span>
            );
          },
        },
      },
      {
        // İşe alınan 0 → backend null → "—" (0 DEĞİL).
        name: 'costPerHire',
        label: t('atsReport.costPerHire'),
        options: {
          filter: false, sort: false,
          customBodyRender: (v, tableMeta) => formatMoney(v, data[tableMeta.rowIndex]?.costCurrency, lang),
        },
      },
      {
        name: 'views',
        label: t('atsReport.views'),
        options: {
          filter: false, sort: false,
          customBodyRender: (v) => (v === null || v === undefined ? EMPTY : Number(v)),
        },
      },
      {
        name: 'uniqueViews',
        label: t('atsReport.uniqueViews'),
        options: {
          filter: false, sort: false,
          customBodyRender: (v) => (v === null || v === undefined ? EMPTY : Number(v)),
        },
      },
      {
        // başvuru / tekil görüntülenme; payda 0 → null → "—".
        name: 'conversionRate',
        label: t('atsReport.conversionRate'),
        options: {
          filter: false, sort: false,
          customBodyRender: (v) => formatRate(v, lang),
        },
      },
    ];
  }, [t, lang, data]);
}
